import { ChangeDetectorRef, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { Billing } from '@core/models/billing/billing';
import { BillingCurrency } from '@core/models/billing/billing-currency';
import { BillingPaymentType } from '@core/models/billing/billing-payment-type';
import { BillingSubscription } from '@core/models/billing/billing-subscription';
import { ToastService } from '@core/state/toast/toast.service';
import { FORMAT } from '@core/utils/formats';
import { $log } from '@core/utils/logger';
import { isNullOrUndefined } from '@core/utils/object';
import { BillingService } from '@features/billing/billing.service';
import { TranslateService } from '@ngx-translate/core';
import { billingAddress } from '@shared/validators/billing-address.validator';
import { OSSValidators } from '@shared/validators/oss-validators';
import { getWindow } from '@vendor/window/window';
import * as moment from 'moment';
import { Subject, combineLatest, take, takeUntil } from 'rxjs';

@Injectable()
export class BaseBillingDialog {
    public isLoading: boolean = false;
    public billingForm: FormGroup;
    public error: any;
    public cardNumber: any;
    public cardExpiry: any;
    public cardCvc: any;
    public billingHandler: any = this.onChange.bind(this);
    public isStripeInitError: boolean = false;
    public isReady: boolean = false;
    public isFormReady: boolean = false;
    public maintainTrial: boolean = false;
    public hasChanges: boolean = false;
    public currentSubscription: BillingSubscription;
    public isSubmitted: boolean = false;
    public isIHDSubscription: boolean;

    public paymentMethod: BillingPaymentType;
    public BillingPaymentType = BillingPaymentType;
    public isCreditCardPayment: boolean;

    protected hasBillingDetails: boolean = false;
    protected isStripeReady: boolean = false;
    protected stripe: any;
    public elements: any;
    protected stripeErrorDesc: any;
    protected origValue: any;
    protected trialEnd: string;
    protected destroyed$: Subject<void> = new Subject();

    protected elementStyles = {
        base: {
            color: '#32325D',
            fontWeight: 500,
            fontSize: '12px',
            fontFamily: 'Source Code Pro, Consolas, Menlo, monospace',
            height: '20px',
            lineHeight: '1.628571',

            '::placeholder': {
                color: '#AAB5BC',
                fontSize: '12px',
                fontWeight: 300,
                fontFamily:
                    'Open Sans, Helvetica Neue, Helvetica, Arial, sans-serif',
            },

            ':-webkit-autofill': {
                color: '#AAB5BC',
            },
        },
        invalid: {
            color: '#AAB5BC',

            '::placeholder': {
                color: '#AAB5BC',
            },
        },
    };

    protected elementClasses = {
        focus: 'focused',
        empty: 'empty',
        invalid: 'invalid',
    };

    protected retryTimer: any;

    constructor(
        protected billingService: BillingService,
        protected toastService: ToastService,
        // protected intercomService: IntercomService,
        protected sanitizer: DomSanitizer,
        protected translateService: TranslateService,
        protected cdr: ChangeDetectorRef
    ) {}

    init() {
        this.billingService
            .getSubscription()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((subscription: BillingSubscription) => {
                this.currentSubscription = subscription;
                if (this.currentSubscription) {
                    this.trialEnd = moment(
                        this.currentSubscription.trial_end,
                        FORMAT.DATETIME_SERVER
                    ).format(FORMAT.DATE_LONG);
                }

                this.cdr.markForCheck();
            });

        this.billingService
            .isIHDSubscription()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((isIHDSubscription: boolean) => {
                this.isIHDSubscription = isIHDSubscription;
                this.cdr.markForCheck();
            });

        combineLatest([
            this.billingService.getBilling(),
            this.billingService.hasOnlyIHDSubscription(),
        ])
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                ([ billing, hasOnlyIHDSubscription ]: [ Billing, boolean ]) => {
                    // we want to set the default payment type only to begin with depending on the type of
                    // subscription. But this selector also triggers after the subscription is successful just before component gets destroyed
                    // and in case of credit card it tries to init stripe again throwing an error in console
                    if (!this.isSubmitted && !this.billingForm.valid) {
                        const paymentType =
                            hasOnlyIHDSubscription &&
                            billing?.currency === BillingCurrency.USD
                                ? BillingPaymentType.ACH
                                : BillingPaymentType.CREDIT_CARD;

                        this.setPaymentType(paymentType);
                    }

                    this.cdr.markForCheck();
                }
            );

        this.billingService
            .hasBillingDetails()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((hasBillingDetails: boolean) => {
                this.hasBillingDetails = hasBillingDetails;
            });
    }

    onChange({ error }) {
        if (error) {
            this.error = error.message;
        } else {
            this.error = null;
        }
        this.cdr.markForCheck();
    }

    setPaymentType(value) {
        this.paymentMethod = value;
        this.isCreditCardPayment = value === BillingPaymentType.CREDIT_CARD;

        if (this.isCreditCardPayment) {
            this.initStripe();
        }
    }

    protected initStripeForm() {
        if (this.elements) {
            this.cardNumber = this.elements.create('cardNumber', {
                style: this.elementStyles,
                classes: this.elementClasses,
                placeholder: 'Card Number',
            });
            this.cardNumber.mount('#card-number');
            this.cardNumber.addEventListener('change', this.billingHandler);

            this.cardExpiry = this.elements.create('cardExpiry', {
                style: this.elementStyles,
                classes: this.elementClasses,
                placeholder: 'Expiration',
            });
            this.cardExpiry.mount('#card-expiry');
            this.cardExpiry.addEventListener('change', this.billingHandler);

            this.cardCvc = this.elements.create('cardCvc', {
                style: this.elementStyles,
                classes: this.elementClasses,
                placeholder: 'CVC',
            });
            this.cardCvc.mount('#card-cvc');
            this.cardCvc.addEventListener('change', this.billingHandler);
            this.isReady = true;
        } else {
            $log.error('this.elements wasnt available..');
            this.isStripeInitError = true;
        }

        this.cdr.detectChanges();
    }

    protected retryInit() {
        // force stripe initalisation to happen by ensuring the subscription
        // for get key is triggered which in-turn initializes stripe

        if (!this.retryTimer) {
            this.retryTimer = setTimeout(() => {
                this.billingService
                    .isKeyFetching()
                    .pipe(take(1))
                    .subscribe((isFetching: boolean) => {
                        if (!isFetching) {
                            $log.info('refetching key');
                            this.billingService.fetchKey();
                        }
                    });
            }, 2000);
        }
    }

    protected initStripe() {
        this.isStripeInitError = false;
        this.cdr.markForCheck();
        this.billingService
            .fetchStripeKey()
            .pipe(take(1))
            .subscribe((key) => {
                if (key) {
                    // Define Stripe config
                    try {

                        const Stripe = (getWindow() as any).Stripe;

                        if (Stripe) {
                            this.stripe = Stripe(key?.billing_key.public_key);
                            this.elements = this.stripe.elements();
                            this.isStripeReady = true;

                            if (this.isFormReady) {
                                this.initStripeForm();
                                this.isStripeInitError = false;
                            }
                        } else {
                            // Stripe not loaded
                            $log.error('stripe not loaded..');
                            this.isStripeInitError = true;
                        }
                    } catch (error) {
                        $log.error('strip error: ', error);
                        this.isStripeInitError = true;
                        this.cdr.markForCheck();
                    }
                }
            });

        this.stripeErrorDesc = this.sanitizer.bypassSecurityTrustHtml(
            this.translateService.instant('Errors.Stripe.Desc')
        );
    }

    protected createForm() {
        this.billingForm = new FormGroup({
            first_name: new FormControl('', Validators.required),
            last_name: new FormControl('', Validators.required),
            email: new FormControl('', [
                Validators.required,
                OSSValidators.email(),
            ]),
            address: new FormGroup(
                {
                    line1: new FormControl('', Validators.required),
                    line2: new FormControl(''),
                    city: new FormControl('', Validators.required),
                    state: new FormControl('', Validators.required),
                    postal_code: new FormControl('', Validators.required),
                    country: new FormControl('', Validators.required),
                },
                billingAddress()
            ),
        });

        this.billingForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe((data: any) => {
                this.hasChanges = this.hasAddressChanges(this.origValue, data);
                this.cdr.markForCheck();
            });
    }

    protected processStripeResponse(stripeResponse, billingDetails, dialogRef) {
        if (stripeResponse) {
            // If it's successful, call OSS API to update the details
            if (!stripeResponse.error) {

                // this.intercomService.trackEvent(
                //     INTERCOM_EVENT.ATTENDANCE_PAYMENT_ADDED
                // );

                if (
                    this.currentSubscription ||
                    isNullOrUndefined(billingDetails.plan)
                ) {
                    // Update existing subscription or just payment details
                    this.billingService.updateBilling(billingDetails).subscribe(
                        (response) => {
                            dialogRef.close({ success: true, response });
                        },
                        (response) => {
                            // ERROR: Update failed.
                            // Define payment gateway error as required
                            let billingError: any;
                            try {
                                billingError = JSON.parse(
                                    response.payload.error.message
                                );
                            } catch (e) {
                                billingError = response.payload.error;
                            }

                            this.toastService.showError(billingError.message);
                            this.isLoading = false;
                            this.cdr.markForCheck();
                        }
                    );
                } else {
                    // New subscription
                    this.billingService
                        .createBilling(billingDetails.plan)
                        .subscribe(
                            (response) => {
                                dialogRef.close({ success: true, response });
                            },
                            (response) => {
                                // ERROR: Update failed.
                                // Define payment gateway error as required
                                let billingError: any;
                                try {
                                    billingError = JSON.parse(
                                        response.payload.error.message
                                    );
                                } catch (e) {
                                    billingError = response.payload.error;
                                }

                                this.toastService.showError(
                                    billingError.message
                                );
                                this.isLoading = false;
                                this.cdr.markForCheck();
                            }
                        );
                }
            } else {
                // Reset the loading flag, during the validation error
                this.isLoading = false;
                this.cdr.markForCheck();
                this.toastService.showError(stripeResponse.error.message);

                // If it's a validation error, handle in the dialog box. Else, show an error message
                if (stripeResponse.error.type !== 'validation_error') {
                    dialogRef.close({
                        success: false,
                        response: stripeResponse.error,
                    });
                }
            }
        } else {
            // ERROR: Stripe failed
            this.isLoading = true;
        }
    }

    protected destroy() {
        if (this.isFormReady && this.isStripeReady) {
            if (this.cardNumber) {
                this.cardNumber.removeEventListener(
                    'change',
                    this.billingHandler
                );
                this.cardNumber.destroy();
            }

            if (this.cardExpiry) {
                this.cardExpiry.removeEventListener(
                    'change',
                    this.billingHandler
                );
                this.cardExpiry.destroy();
            }

            if (this.cardCvc) {
                this.cardCvc.removeEventListener('change', this.billingHandler);
                this.cardCvc.destroy();
            }
        }

        clearTimeout(this.retryTimer);

        this.destroyed$.next();
    }

    private hasAddressChanges(originalAddress: any, newAddress: any) {
        return !(
            (!originalAddress && !newAddress) ||
            (originalAddress &&
                newAddress &&
                originalAddress.first_name === newAddress.first_name &&
                originalAddress.last_name === newAddress.last_name &&
                originalAddress.email === newAddress.email &&
                originalAddress.source === newAddress.source &&
                ((originalAddress.address &&
                    newAddress.address &&
                    originalAddress.address.line1 ===
                        newAddress.address.line1 &&
                    originalAddress.address.line2 ===
                        newAddress.address.line2 &&
                    originalAddress.address.city === newAddress.address.city &&
                    originalAddress.address.state ===
                        newAddress.address.state &&
                    originalAddress.address.postal_code ===
                        newAddress.address.postal_code &&
                    originalAddress.address.country ===
                        newAddress.address.country) ||
                    (!originalAddress.address && !newAddress.address)))
        );
    }
}